import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link2, Lock, TrendingUp } from 'lucide-react';

const HowItWorksSection = () => {

    const dustParticlesValues = useMemo(() => {
        return Array.from({ length: 30 }).map((_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * 2 + 1 + 'px',
            duration: Math.random() * 15 + 10,
            delay: Math.random() * 5,
        }));
    }, []);

    const steps = [
        {
            icon: <Link2 size={32} className="text-purple-400" />,
            title: "1. Create a Link",
            description: "Paste your destination URL that you want to hide. This could be exclusive content, downloads, or group invites."
        },
        {
            icon: <Lock size={32} className="text-purple-400" />,
            title: "2. Set Requirements",
            description: "Choose the actions users must take to unlock the link, like subscribing to YouTube, joining a Telegram channel, or following on X."
        },
        {
            icon: <TrendingUp size={32} className="text-purple-400" />,
            title: "3. Share & Grow",
            description: "Share your Linkey URL anywhere. Users complete your requirements to access the content, turning traffic into genuine followers."
        }
    ];

    return (
        <section id="how-it-works" className="relative w-full pt-16 pb-32 px-4 flex flex-col items-center justify-center border-t border-white/5 bg-[#050505] overflow-hidden">
            <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center w-full">
                <motion.h2 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-400 mb-8 text-center"
                >
                    How it Works
                </motion.h2>
                
                <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="text-xl md:text-3xl text-gray-400 max-w-4xl text-center mb-24"
                >
                    Three simple steps to start converting your audience into real growth.
                </motion.p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                    {steps.map((step, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 + index * 0.1 }}
                            className="relative group p-8 rounded-2xl border border-white/5 bg-white/[0.02] backdrop-blur-sm hover:bg-white/[0.04] transition-all duration-300"
                        >
                            {}
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-purple-500/0 to-purple-500/0 group-hover:from-purple-500/5 group-hover:to-transparent transition-all duration-500 pointer-events-none" />
                            
                            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-purple-500/10 border border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.15)] group-hover:shadow-[0_0_30px_rgba(168,85,247,0.3)] transition-shadow duration-300">
                                {step.icon}
                            </div>
                            
                            <h3 className="text-xl font-bold text-white mb-4">
                                {step.title}
                            </h3>
                            
                            <p className="text-gray-400 leading-relaxed">
                                {step.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default HowItWorksSection;